import React, { useState } from 'react';
import { orderService } from '../services/orderService';
import { Search, Package, CheckCircle2, Truck, XCircle } from 'lucide-react';

const STATUS_CONFIG = {
  Pending: { label: "Pending", bg: "bg-yellow-100 text-yellow-800" },
  Processing: { label: "Processing", bg: "bg-purple-100 text-purple-800" },
  Shipped: { label: "Out for Delivery", bg: "bg-blue-100 text-blue-800" },
  Delivered: { label: "Delivered", bg: "bg-green-100 text-green-800" },
  Cancelled: { label: "Cancelled", bg: "bg-red-100 text-red-800" },
};

const STATUS_STEPS = [
  { status: "Pending", label: "Order Placed", icon: Package },
  { status: "Shipped", label: "Out for Delivery", icon: Truck },
  { status: "Delivered", label: "Delivered", icon: CheckCircle2 },
];

export default function TrackOrderPage() {
  const [searchType, setSearchType] = useState('id'); // 'id' | 'phone'
  const [input, setInput] = useState('');
  const [searching, setSearching] = useState(false);
  const [order, setOrder] = useState(null);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState('');

  async function handleSearch(e) {
    if (e) e.preventDefault();
    if (!input.trim()) return;
    setSearching(true);
    setSearched(true);
    setOrder(null);
    setError('');
    try {
      const orderNum = searchType === 'id' ? input.trim() : '';
      const phone = searchType === 'phone' ? input.trim() : '';
      const res = await orderService.trackOrder(orderNum, phone);
      setOrder(res || null);
    } catch (err) {
      console.error(err);
      setError('We could not check your order right now. Please try again.');
    } finally {
      setSearching(false);
    }
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-16">
      <div className="text-center mb-8">
        <h1 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">Track Your Order</h1>
        <p className="text-slate-500 text-sm mt-1">Enter your Order ID or Phone Number to see your delivery status.</p>
      </div>

      <div className="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200 shadow-md mb-8">
        {/* Search Type Selector */}
        <div className="flex gap-2 mb-4">
          <button
            type="button"
            onClick={() => { setSearchType('id'); setInput(''); setOrder(null); setSearched(false); setError(''); }}
            className={`flex-1 py-2.5 rounded-xl text-xs font-bold border transition-colors ${
              searchType === 'id'
                ? "bg-blue-900 text-white border-blue-900"
                : "bg-white text-slate-500 border-slate-200 hover:border-blue-900"
            }`}
          >
            By Order ID
          </button>
          <button
            type="button"
            onClick={() => { setSearchType('phone'); setInput(''); setOrder(null); setSearched(false); setError(''); }}
            className={`flex-1 py-2.5 rounded-xl text-xs font-bold border transition-colors ${
              searchType === 'phone'
                ? "bg-blue-900 text-white border-blue-900"
                : "bg-white text-slate-500 border-slate-200 hover:border-blue-900"
            }`}
          >
            By Phone Number
          </button>
        </div>

        {/* Input Form */}
        <form onSubmit={handleSearch} className="flex gap-2">
          <input
            type="text"
            required
            placeholder={searchType === 'id' ? "Enter Order ID (e.g. A1S-123456-7890)" : "Enter 10-digit Phone Number"}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            className="flex-1 px-4 py-3 rounded-xl border border-slate-200 text-sm focus:ring-2 focus:ring-orange-500 outline-none"
          />
          <button
            type="submit"
            disabled={searching || !input.trim()}
            className="px-6 py-3 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold text-xs rounded-xl shadow-md transition disabled:opacity-50 flex items-center gap-1.5"
          >
            <Search size={14} /> {searching ? 'Searching...' : 'Track'}
          </button>
        </form>

        {error && (
          <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-center text-xs font-semibold text-rose-700">
            {error}
          </div>
        )}

        {/* Result Card */}
        {searched && !searching && !order && !error && (
          <div className="text-center py-10 mt-6 border-t border-slate-100 text-slate-400 text-xs">
            <Package size={36} className="mx-auto mb-2 text-slate-300" />
            No orders found matching your input.
          </div>
        )}

        {order && (
          <div className="mt-8 pt-6 border-t border-slate-100">
            <div className="bg-blue-900 text-white px-6 py-4 rounded-2xl flex items-center justify-between mb-5 shadow-sm">
              <div>
                <p className="text-[10px] uppercase text-white/60">Order ID</p>
                <p className="font-mono font-bold text-lg">{order.order_number}</p>
              </div>
              <span className={`px-3 py-1 rounded-full text-xs font-bold border-0 ${(STATUS_CONFIG[order.status] || STATUS_CONFIG.Pending).bg}`}>
                {(STATUS_CONFIG[order.status] || STATUS_CONFIG.Pending).label}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-4 text-xs bg-slate-50 p-5 rounded-2xl mb-6">
              <div>
                <p className="text-slate-400 font-semibold mb-0.5">Customer</p>
                <p className="font-bold text-slate-900 text-sm">{order.customer_name}</p>
              </div>
              <div>
                <p className="text-slate-400 font-semibold mb-0.5">Phone Number</p>
                <p className="font-bold text-slate-900 text-sm">{order.phone}</p>
              </div>
              <div>
                <p className="text-slate-400 font-semibold mb-0.5">Operator & Plan</p>
                <p className="font-bold text-slate-900 text-sm">{order.operator} - {order.plan_name} (₹{order.plan_price})</p>
              </div>
              <div>
                <p className="text-slate-400 font-semibold mb-0.5">Delivery Address</p>
                <p className="font-bold text-slate-900 text-sm">{order.address}, {order.city}</p>
              </div>
            </div>

            {/* Stepper */}
            {order.status !== 'Cancelled' ? (
              <div className="flex items-center justify-between px-2 pt-2">
                {STATUS_STEPS.map((s, idx) => {
                  const sequence = ["Pending", "Shipped", "Delivered"];
                  const currentIndex = sequence.indexOf(order.status);
                  const isCompleted = idx <= (currentIndex === -1 ? 0 : currentIndex);
                  const Icon = s.icon;
                  return (
                    <div key={s.status} className="flex flex-col items-center flex-1">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center border-2 transition-all ${
                        isCompleted ? "bg-orange-500 border-orange-500 text-white" : "bg-white border-slate-200 text-slate-300"
                      }`}>
                        <Icon size={16} />
                      </div>
                      <p className={`text-[11px] mt-1 font-semibold ${isCompleted ? "text-orange-600" : "text-slate-400"}`}>
                        {s.label}
                      </p>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex items-center gap-2 text-rose-700 bg-rose-50 p-4 rounded-xl text-xs font-bold">
                <XCircle size={18} /> This order has been cancelled.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}